import { useState, useRef, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAppStore } from '@/store/appStore'
import { getAiProvider, getPpmTools, addAuditEntry, getAuditLog, getDebugLog, clearDebugLog } from '@/services/aiService'
import type { AiTool, AiToolPreview, DebugLogEntry } from '@/services/aiService'
import { useFlag } from '@/hooks/useFlag'
// Browser speech, for dictating into the chat box. The same hook Quick
// Capture uses, so the mic behaves identically in both places.
import { useSpeechRecognition } from '@/modules/crm/hooks/useSpeechRecognition'
import type { AiMessage, AiToolCall } from '@/types/ppm'
import { generateId, cn } from '@/lib/utils'
import { appFullName } from '@/config/appIdentity'
import { StatusReportView } from './StatusReportView'
import {
  isItTicketingPath, buildItTicketingSystemPrompt, getItTicketingAgentTools, getItTicketingSuggestions,
} from '@/modules/it-ticketing/services/agentContext'
import type { StatusReportData } from './StatusReportView'
import {
  X,
  Send,
  Sparkles,
  Bot,
  User,
  Copy,
  Trash2,
  CheckCircle2,
  XCircle,
  Clock,
  FileText,
  ChevronDown,
  ChevronUp,
  Code2,
  ArrowUp,
  ArrowDown,
  Trash,
  Mic,
  MicOff,
  Ticket,
} from 'lucide-react'

// A tool's own description of what Confirm will do (AiTool.preview), loaded
// once when its card first shows as pending. Tools without one keep the
// plain "requires your confirmation" card.
function useToolPreview(toolCall: AiToolCall, preview: AiTool['preview']) {
  const [state, setState] = useState<{ loading: boolean; data: AiToolPreview | null }>({ loading: !!preview, data: null })
  useEffect(() => {
    if (!preview || toolCall.status !== 'pending') return
    let cancelled = false
    preview(toolCall.input)
      .then((data) => { if (!cancelled) setState({ loading: false, data }) })
      .catch((e: unknown) => {
        if (!cancelled) setState({ loading: false, data: { summary: '', blocking: e instanceof Error ? e.message : 'Couldn’t check what this will change.' } })
      })
    return () => { cancelled = true }
    // The input of a proposed call never changes; status flips once.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolCall.id, toolCall.status])
  return state
}

const PREVIEW_ITEMS = 8

export function ToolCallCard({ toolCall, preview, onConfirm, onReject }: {
  toolCall: AiToolCall
  preview?: AiTool['preview']
  onConfirm: () => void
  onReject: () => void
}) {
  const p = useToolPreview(toolCall, preview)
  const blocked = !!preview && (p.loading || !!p.data?.blocking)
  return (
    <div className="mt-2 border border-border rounded-lg overflow-hidden bg-surface-muted">
      <div className="flex items-center gap-2 px-3 py-2 bg-surface-muted border-b border-border">
        <FileText size={14} className="text-brand-500" />
        <span className="text-body-sm font-medium">{toolCall.name.replace(/_/g, ' ')}</span>
        <span className={cn(
          'ml-auto text-[10px] uppercase font-medium tracking-wider px-1.5 py-0.5 rounded',
          toolCall.status === 'executed' && 'bg-status-green-bg text-status-green',
          toolCall.status === 'pending' && 'bg-status-yellow-bg text-status-yellow',
          toolCall.status === 'confirmed' && 'bg-status-blue-bg text-status-blue',
          toolCall.status === 'rejected' && 'bg-status-red-bg text-status-red',
        )}>
          {toolCall.status}
        </span>
      </div>

      {toolCall.status === 'pending' && preview && (
        <div className="px-3 pt-2 text-body-sm">
          {p.loading ? (
            <span className="text-text-tertiary">Checking what this will change...</span>
          ) : p.data?.blocking ? (
            <span className="text-status-red">{p.data.blocking}</span>
          ) : p.data ? (
            <>
              <div className="text-text-primary">{p.data.summary}</div>
              {p.data.items && p.data.items.length > 0 && (
                <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-[12px] text-text-secondary">
                  {p.data.items.slice(0, PREVIEW_ITEMS).map((item) => <li key={item}>{item}</li>)}
                  {(p.data.total ?? p.data.items.length) > PREVIEW_ITEMS && (
                    <li className="text-text-tertiary">and {(p.data.total ?? p.data.items.length) - PREVIEW_ITEMS} more</li>
                  )}
                </ul>
              )}
            </>
          ) : null}
        </div>
      )}

      {toolCall.status === 'pending' && (
        <div className="px-3 py-2 flex items-center gap-2">
          <span className="text-body-sm text-text-secondary flex-1">
            This action requires your confirmation before execution.
          </span>
          <button
            onClick={onConfirm}
            disabled={blocked}
            className="flex items-center gap-1 px-3 py-1.5 rounded bg-status-green text-white text-body-sm font-medium hover:bg-green-700 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
          >
            <CheckCircle2 size={14} />
            Confirm
          </button>
          <button
            onClick={onReject}
            className="flex items-center gap-1 px-3 py-1.5 rounded border border-border text-body-sm font-medium hover:bg-surface-muted transition-colors"
          >
            <XCircle size={14} />
            Reject
          </button>
        </div>
      )}

      {toolCall.output && (
        <div className="px-3 py-2 text-body-sm text-text-secondary">
          <pre className="whitespace-pre-wrap text-[11px] bg-white rounded p-2 border border-border max-h-32 overflow-y-auto">
            {JSON.stringify(toolCall.output, null, 2)}
          </pre>
        </div>
      )}
    </div>
  )
}

function MessageBubble({ message, findTool, onToolConfirm, onToolReject }: {
  message: AiMessage
  findTool?: (name: string) => AiTool | undefined
  onToolConfirm: (toolCallId: string) => void
  onToolReject: (toolCallId: string) => void
}) {
  const isUser = message.role === 'user'

  // Check if this message contains a visual status report
  const statusReportData = getStatusReportFromMessage(message)

  return (
    <div className={cn('flex gap-3 animate-slide-in-up', isUser && 'flex-row-reverse')}>
      <div className={cn(
        'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5',
        isUser ? 'bg-brand-500' : 'bg-amber-100',
      )}>
        {isUser ? <User size={14} className="text-white" /> : <Bot size={14} className="text-amber-700" />}
      </div>
      <div className={cn('flex-1', isUser ? 'max-w-[85%] text-right' : 'max-w-full')}>
        {/* If this is a status report, render the visual component */}
        {statusReportData ? (
          <StatusReportView data={statusReportData} />
        ) : (
          <div className={cn(
            'inline-block text-left rounded-lg px-3 py-2 text-body-sm',
            isUser
              ? 'bg-brand-500 text-white rounded-tr-sm'
              : 'bg-white border border-border rounded-tl-sm',
          )}>
            {/* Render markdown-style content */}
            <div
              className="whitespace-pre-wrap [&_strong]:font-semibold [&_h2]:text-heading-sm [&_h2]:font-display [&_h2]:mt-2 [&_h2]:mb-1 [&_h3]:text-body-md [&_h3]:font-semibold [&_h3]:mt-2"
              dangerouslySetInnerHTML={{
                __html: message.content
                  .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                  .replace(/^## (.*?)$/gm, '<h2>$1</h2>')
                  .replace(/^### (.*?)$/gm, '<h3>$1</h3>')
                  .replace(/^- (.*?)$/gm, '<div class="flex gap-1.5 ml-1"><span class="text-text-tertiary">-</span><span>$1</span></div>')
                  .replace(/\[([^\]]+)\]\((\/[^)]+)\)/g, '<a href="$2" class="text-brand-500 underline hover:text-brand-700 font-medium">$1</a>')
                  .replace(/\n\n/g, '<br/><br/>')
              }}
            />
          </div>
        )}

        {/* Tool calls (hide for visual status reports) */}
        {!statusReportData && message.toolCalls?.map((tc) => (
          <ToolCallCard
            key={tc.id}
            toolCall={tc}
            preview={findTool?.(tc.name)?.preview}
            onConfirm={() => onToolConfirm(tc.id)}
            onReject={() => onToolReject(tc.id)}
          />
        ))}

        {/* Citations */}
        {message.citations && message.citations.length > 0 && (
          <div className="mt-1.5 flex flex-wrap gap-1">
            {message.citations.map((c, i) => (
              <span key={i} className="inline-flex items-center text-[10px] px-1.5 py-0.5 rounded bg-brand-50 text-brand-600">
                {c.field}: {c.value}
              </span>
            ))}
          </div>
        )}

        <div className="text-[10px] text-text-tertiary mt-1">
          {new Date(message.timestamp).toLocaleTimeString()}
        </div>
      </div>
    </div>
  )
}

/** Extract StatusReportData from a message if it contains a visual status report */
function getStatusReportFromMessage(message: AiMessage): StatusReportData | null {
  if (message.role !== 'assistant') return null

  // Check for the sentinel marker from mock provider or structured tool output
  const isStatusReportMessage = message.content === '__STATUS_REPORT__'

  // Also check tool calls for draft_status_report output
  const statusReportToolCall = message.toolCalls?.find(
    tc => tc.name === 'draft_status_report' && tc.status === 'executed' && tc.output
  )

  if (isStatusReportMessage && statusReportToolCall?.output) {
    const output = statusReportToolCall.output as Record<string, unknown>
    // Validate it has the expected shape
    if (output.project && output.aiNarrative && output.schedule && output.cost) {
      return output as unknown as StatusReportData
    }
  }

  // For Claude provider: check if any tool call returned structured report data
  if (statusReportToolCall?.output) {
    const output = statusReportToolCall.output as Record<string, unknown>
    if (output.project && output.aiNarrative && output.schedule && output.cost) {
      return output as unknown as StatusReportData
    }
  }

  return null
}

function DebugEntry({ entry }: { entry: DebugLogEntry }) {
  const [expanded, setExpanded] = useState(false)
  const isRequest = entry.direction === 'request'

  return (
    <div className="border border-border rounded text-[11px]">
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(
          'w-full flex items-center gap-2 px-2 py-1.5 text-left',
          isRequest ? 'bg-blue-50' : 'bg-green-50',
        )}
      >
        {isRequest ? (
          <ArrowUp size={10} className="text-blue-600 flex-shrink-0" />
        ) : (
          <ArrowDown size={10} className="text-green-600 flex-shrink-0" />
        )}
        <span className={cn('font-mono font-medium', isRequest ? 'text-blue-700' : 'text-green-700')}>
          {isRequest ? 'REQUEST' : 'RESPONSE'}
        </span>
        <span className="text-text-tertiary ml-auto font-mono">
          {new Date(entry.timestamp).toLocaleTimeString()}
        </span>
        {expanded ? <ChevronUp size={10} /> : <ChevronDown size={10} />}
      </button>
      {expanded && (
        <pre className="p-2 overflow-auto max-h-80 text-[10px] font-mono bg-white whitespace-pre-wrap break-all">
          {JSON.stringify(entry.data, null, 2)}
        </pre>
      )}
    </div>
  )
}

export function AiPanel() {
  const toggleAiPanel = useAppStore((s) => s.toggleAiPanel)
  const messages = useAppStore((s) => s.aiMessages)
  const addMessage = useAppStore((s) => s.addAiMessage)
  const clearMessages = useAppStore((s) => s.clearAiMessages)
  const currentUser = useAppStore((s) => s.currentUser)
  // Agile AI tools are advertised only when the agile.enabled capability is on.
  const agileEnabled = useFlag<boolean>('agile.enabled') ?? false

  // IT Ticketing mode: anywhere under /it-ticketing the agent narrows to the
  // ticketing persona, tools, and suggestions (see agentContext.ts).
  const { pathname } = useLocation()
  const ticketingMode = isItTicketingPath(pathname)
  const agentTools = () => (ticketingMode ? getItTicketingAgentTools() : getPpmTools({ agileEnabled }))
  // Fall back to the full set: a proposal may predate a route change into or
  // out of IT Ticketing mode.
  const findTool = (name: string): AiTool | undefined =>
    agentTools().find(t => t.name === name) ?? getPpmTools({ agileEnabled }).find(t => t.name === name)
  const sendOptions = () => (ticketingMode ? { systemPrompt: buildItTicketingSystemPrompt(pathname) } : undefined)
  const suggestions = ticketingMode
    ? getItTicketingSuggestions(pathname)
    : ['What\'s in my workflow queue?', 'Show at-risk projects', 'Draft status report for this project', 'Why is CPI down?']

  const [input, setInput] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [auditOpen, setAuditOpen] = useState(false)
  const [debugOpen, setDebugOpen] = useState(false)
  const [expanded] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Dictation appends to whatever is already typed, so a person can start
  // with the keyboard and finish with their voice.
  const speech = useSpeechRecognition({
    onFinal: (text) => setInput((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text)),
  })

  // Auto-expand panel when a status report is present
  const hasStatusReport = messages.some(m => {
    if (m.role !== 'assistant') return false
    const tc = m.toolCalls?.find(t => t.name === 'draft_status_report' && t.status === 'executed' && t.output)
    if (!tc) return false
    const o = tc.output as Record<string, unknown>
    return !!(o.project && o.aiNarrative)
  })
  const panelWidth = expanded || hasStatusReport ? 'w-[720px]' : 'w-96'

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  // Consume any pending prompt that was queued before the panel opened
  const pendingAiPrompt = useAppStore((s) => s.pendingAiPrompt)
  const clearPendingAiPrompt = useAppStore((s) => s.clearPendingAiPrompt)
  useEffect(() => {
    if (pendingAiPrompt && !isLoading) {
      clearPendingAiPrompt()
      sendMessage(pendingAiPrompt)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingAiPrompt])

  async function sendMessage(text: string) {
    if (!text || isLoading) return

    const userMsg: AiMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    }
    addMessage(userMsg)
    setInput('')
    setIsLoading(true)

    addAuditEntry({
      userId: currentUser.id,
      action: 'ai_query',
      entityType: 'conversation',
      entityId: userMsg.id,
      details: { query: text },
      aiGenerated: false,
      confirmed: true,
    })

    try {
      const provider = getAiProvider()
      const allMessages = [...messages, userMsg]
      const response = await provider.sendMessage(allMessages, agentTools(), sendOptions())
      addMessage(response)

      addAuditEntry({
        userId: currentUser.id,
        action: 'ai_response',
        entityType: 'conversation',
        entityId: response.id,
        details: {
          responseLength: response.content.length,
          toolCalls: response.toolCalls?.map(tc => tc.name) ?? [],
        },
        aiGenerated: true,
        confirmed: false,
      })
    } catch {
      addMessage({
        id: generateId(),
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString(),
      })
    } finally {
      setIsLoading(false)
    }
  }

  async function handleSend() {
    const text = input.trim()
    if (!text || isLoading) return
    sendMessage(text)
  }

  async function handleToolConfirm(toolCallId: string) {
    const lastMsg = messages[messages.length - 1]
    if (!lastMsg?.toolCalls) return

    const targetCall = lastMsg.toolCalls.find(tc => tc.id === toolCallId)
    if (!targetCall) return

    // Find the tool definition. Confirming a write tool actually executes
    // the handler now (previously only flipped the status — which is why
    // the chatbot appeared to "create" things without writing to the DB).
    // Fall back to the full set: the proposal may predate a route change
    // into or out of IT Ticketing mode.
    const tool = findTool(targetCall.name)

    addAuditEntry({
      userId: currentUser.id,
      action: 'ai_tool_confirmed',
      entityType: 'tool_call',
      entityId: toolCallId,
      details: { toolName: targetCall.name },
      aiGenerated: true,
      confirmed: true,
    })

    let output: Record<string, unknown> | undefined
    let executionError: string | null = null
    if (tool) {
      try {
        output = await tool.handler(targetCall.input)
      } catch (e) {
        executionError = e instanceof Error ? e.message : 'Tool execution failed'
      }
    }

    const finalStatus: AiToolCall['status'] = executionError ? 'rejected' : 'executed'
    const updatedCalls = lastMsg.toolCalls.map(tc =>
      tc.id === toolCallId ? { ...tc, status: finalStatus, output } : tc
    )
    const updatedMsg = { ...lastMsg, toolCalls: updatedCalls }
    let newMessages = [...messages.slice(0, -1), updatedMsg]

    // Append a follow-up assistant message summarizing the outcome.
    if (executionError) {
      newMessages = [...newMessages, {
        id: generateId(),
        role: 'assistant',
        content: `**${targetCall.name.replace(/_/g, ' ')}** failed: ${executionError}`,
        timestamp: new Date().toISOString(),
      }]
    } else if (output && (output as { error?: string }).error) {
      newMessages = [...newMessages, {
        id: generateId(),
        role: 'assistant',
        content: `**${targetCall.name.replace(/_/g, ' ')}** could not complete: ${(output as { error: string }).error}`,
        timestamp: new Date().toISOString(),
      }]
    } else if (output && (output as { message?: string }).message) {
      newMessages = [...newMessages, {
        id: generateId(),
        role: 'assistant',
        content: String((output as { message: string }).message),
        timestamp: new Date().toISOString(),
      }]
    }

    useAppStore.setState({ aiMessages: newMessages })
  }

  function handleToolReject(toolCallId: string) {
    const lastMsg = messages[messages.length - 1]
    if (!lastMsg?.toolCalls) return

    const updatedCalls = lastMsg.toolCalls.map(tc =>
      tc.id === toolCallId ? { ...tc, status: 'rejected' as const } : tc
    )
    const updatedMsg = { ...lastMsg, toolCalls: updatedCalls }
    const newMessages = [...messages.slice(0, -1), updatedMsg]
    useAppStore.setState({ aiMessages: newMessages })

    addAuditEntry({
      userId: currentUser.id,
      action: 'ai_tool_rejected',
      entityType: 'tool_call',
      entityId: toolCallId,
      details: { toolName: lastMsg.toolCalls.find(tc => tc.id === toolCallId)?.name },
      aiGenerated: true,
      confirmed: false,
    })
  }

  function handleCopy(content: string) {
    navigator.clipboard.writeText(content.replace(/<[^>]+>/g, ''))
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const auditEntries = getAuditLog()

  return (
    <div className={cn('fixed right-0 top-0 h-full bg-white border-l border-border shadow-modal z-40 flex flex-col animate-slide-in-right transition-all duration-300', panelWidth)}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 h-14 border-b border-border flex-shrink-0">
        <div className="flex items-center gap-2">
          <Sparkles size={18} className="text-amber-500" />
          <span className="text-heading-sm font-display">XPM Agent</span>
          {ticketingMode && (
            <span
              className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-medium text-brand-700"
              title="Focused on IT Ticketing while you're on these pages"
            >
              <Ticket size={12} /> IT Ticketing
            </span>
          )}
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={clearMessages}
            className="h-8 w-8 rounded hover:bg-surface-muted flex items-center justify-center text-text-secondary transition-colors"
            aria-label="Clear chat"
          >
            <Trash2 size={16} />
          </button>
          <button
            onClick={toggleAiPanel}
            className="h-8 w-8 rounded hover:bg-surface-muted flex items-center justify-center text-text-secondary transition-colors"
            aria-label="Close AI panel"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="text-center py-8">
            <div className="w-12 h-12 rounded-full bg-amber-50 flex items-center justify-center mx-auto mb-3">
              <Sparkles size={24} className="text-amber-500" />
            </div>
            <h3 className="text-heading-sm font-display mb-2">
              {ticketingMode ? 'IT Ticketing Agent' : `${appFullName()} Agent`}
            </h3>
            <p className="text-body-sm text-text-secondary mb-4">
              {ticketingMode
                ? 'I can triage the ticket queue, find tickets that are stuck, summarize a ticket, and file, assign, update, or comment on tickets (with your confirmation).'
                : 'I can analyze your portfolio, check your workflow queue, identify risks, and draft reports.'}
            </p>
            <div className="space-y-2">
              {suggestions.map((q) => (
                <button
                  key={q}
                  // Send the text directly: handleSend() reads `input` from this
                  // render's closure, so setInput-then-handleSend sent nothing.
                  onClick={() => sendMessage(q)}
                  className="w-full text-left px-3 py-2 rounded-lg border border-border hover:border-brand-400 hover:bg-brand-50 text-body-sm transition-colors"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg) => (
          <div key={msg.id} className="group relative">
            <MessageBubble
              message={msg}
              findTool={findTool}
              onToolConfirm={handleToolConfirm}
              onToolReject={handleToolReject}
            />
            {msg.role === 'assistant' && (
              <button
                onClick={() => handleCopy(msg.content)}
                className="absolute top-0 right-0 opacity-0 group-hover:opacity-100 h-6 w-6 rounded hover:bg-surface-muted flex items-center justify-center text-text-tertiary transition-all"
                aria-label="Copy response"
              >
                <Copy size={12} />
              </button>
            )}
          </div>
        ))}

        {isLoading && (
          <div className="flex gap-3 animate-fade-in">
            <div className="w-7 h-7 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
              <Bot size={14} className="text-amber-700 animate-pulse-subtle" />
            </div>
            <div className="bg-white border border-border rounded-lg rounded-tl-sm px-3 py-2">
              <div className="flex gap-1">
                <div className="w-2 h-2 rounded-full bg-brand-300 animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 rounded-full bg-brand-300 animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 rounded-full bg-brand-300 animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Audit log toggle */}
      <div className="border-t border-border">
        <button
          onClick={() => setAuditOpen(!auditOpen)}
          className="w-full flex items-center justify-between px-4 py-2 text-body-sm text-text-secondary hover:bg-surface-muted transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <Clock size={14} />
            Audit Log ({auditEntries.length})
          </span>
          {auditOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
        </button>
        {auditOpen && (
          <div className="max-h-40 overflow-y-auto px-4 pb-2 space-y-1">
            {auditEntries.length === 0 ? (
              <div className="text-body-sm text-text-tertiary py-2">No audit entries yet.</div>
            ) : (
              auditEntries.slice(-10).reverse().map((entry) => (
                <div key={entry.id} className="flex items-center gap-2 text-[10px] text-text-tertiary py-0.5">
                  <span className={cn(
                    'w-1.5 h-1.5 rounded-full flex-shrink-0',
                    entry.confirmed ? 'bg-status-green' : 'bg-status-yellow'
                  )} />
                  <span className="font-mono">{new Date(entry.timestamp).toLocaleTimeString()}</span>
                  <span className="truncate">{entry.action}</span>
                  {entry.aiGenerated && <Sparkles size={10} className="text-amber-400 flex-shrink-0" />}
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Debug / Prompt Inspector */}
      <div className="border-t border-border">
        <button
          onClick={() => setDebugOpen(!debugOpen)}
          className="w-full flex items-center justify-between px-4 py-2 text-body-sm text-text-secondary hover:bg-surface-muted transition-colors"
        >
          <span className="flex items-center gap-1.5">
            <Code2 size={14} />
            Prompt Inspector ({getDebugLog().length})
          </span>
          <div className="flex items-center gap-1">
            {getDebugLog().length > 0 && (
              <span
                onClick={(e) => { e.stopPropagation(); clearDebugLog(); }}
                className="text-[10px] text-text-tertiary hover:text-red-600 px-1"
              >
                <Trash size={10} />
              </span>
            )}
            {debugOpen ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </div>
        </button>
        {debugOpen && (
          <div className="max-h-64 overflow-y-auto px-4 pb-2 space-y-1.5">
            {getDebugLog().length === 0 ? (
              <div className="text-body-sm text-text-tertiary py-2">
                No API calls yet. Send a message to see prompts here.
                {getAiProvider().name === 'mock' && (
                  <div className="mt-1 text-amber-600 font-medium">
                    Sample responses are shown. The AI assistant is not turned on for this workspace. Ask your administrator to enable it.
                  </div>
                )}
              </div>
            ) : (
              getDebugLog().map((entry) => (
                <DebugEntry key={entry.id} entry={entry} />
              ))
            )}
          </div>
        )}
      </div>

      {/* Input */}
      <div className="px-4 py-3 border-t border-border flex-shrink-0">
        <div className="flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={speech.listening ? 'Listening...' : (ticketingMode ? 'Ask about tickets...' : 'Ask about your portfolio...')}
            className="flex-1 h-10 min-h-[40px] max-h-24 px-3 py-2 rounded-lg border border-border bg-surface-input text-body-sm resize-none focus:outline-none focus:ring-2 focus:ring-brand-500/30 focus:border-brand-500"
            rows={1}
            aria-label="Message input"
          />
          {/* Dictate into the chat box. Same hook Quick Capture uses, so the
              two behave identically; hidden entirely on a browser with no Web
              Speech API rather than shown as a button that does nothing. */}
          {speech.supported && (
            <button
              onClick={() => (speech.listening ? speech.stop() : speech.start('continuous'))}
              disabled={isLoading}
              className={cn(
                'h-10 w-10 rounded-lg flex items-center justify-center transition-colors flex-shrink-0 border',
                speech.listening
                  ? 'bg-rose-500 border-rose-500 text-white animate-pulse'
                  : 'bg-white border-border text-text-secondary hover:bg-surface-muted',
              )}
              aria-label={speech.listening ? 'Stop dictation' : 'Dictate a message'}
              title={speech.listening ? 'Stop dictation' : 'Dictate a message'}
            >
              {speech.listening ? <MicOff size={16} /> : <Mic size={16} />}
            </button>
          )}
          <button
            onClick={handleSend}
            disabled={!input.trim() || isLoading}
            className="h-10 w-10 rounded-lg bg-brand-500 text-white hover:bg-brand-600 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center transition-colors flex-shrink-0"
            aria-label="Send message"
          >
            <Send size={16} />
          </button>
        </div>
        {speech.interim && (
          <div className="text-[10px] text-text-tertiary italic mt-1">{speech.interim}</div>
        )}
        {speech.error && (
          <div className="text-[10px] text-amber-600 mt-1">{speech.error}</div>
        )}
        <div className="text-[10px] text-text-tertiary mt-1.5 flex items-center justify-between">
          <span>{ticketingMode ? 'AI responses are grounded in your ticket queue.' : 'AI responses are grounded in portfolio data.'}</span>
          <span className={cn(
            'font-mono px-1.5 py-0.5 rounded',
            getAiProvider().name === 'claude' ? 'bg-brand-50 text-brand-600' : 'bg-amber-50 text-amber-600',
          )}>
            {getAiProvider().name === 'claude' ? 'AI assistant' : 'Sample'}
          </span>
        </div>
      </div>
    </div>
  )
}
